import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  VStack,
  Radio,
  RadioGroup,
  Divider,
} from '@chakra-ui/react';

/**
 * Modal for selecting the correct address when multiple matches are found
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Array} props.addressOptions - Array of address options
 * @param {Function} props.onSelect - Function called when an address is selected
 */
const AddressSelectionModal = ({ isOpen, onClose, addressOptions = [], onSelect }) => {
  const [selectedAddress, setSelectedAddress] = React.useState('');

  // Reset selected address when modal opens with new options
  React.useEffect(() => {
    if (isOpen && addressOptions.length > 0) {
      setSelectedAddress(addressOptions[0].id);
    }
  }, [isOpen, addressOptions]);

  const handleSelect = () => {
    const selected = addressOptions.find((opt) => opt.id === selectedAddress);
    console.log('selected', selected);
    if (selected) {
      onSelect(selected);
      onClose();
    }
  };

  // Format the address for display
  const formatAddress = (address) => {
    const parts = [];

    if (address.saon) parts.push(`${address.saon}`);
    if (address.paon) parts.push(`${address.paon}`);
    if (address.street) parts.push(address.street);
    if (address.town) parts.push(address.town);
    if (address.postcode) parts.push(address.postcode);

    return parts.join(', ');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Select Your Address</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={4}>
            We found multiple properties at this location. Please select the correct address:
          </Text>

          <RadioGroup value={selectedAddress} onChange={setSelectedAddress}>
            <VStack align='start' spacing={4}>
              {addressOptions.map((option) => (
                <Box key={option.id} w='100%' p={3} borderWidth='1px' borderRadius='md'>
                  <Radio value={option.id} w='100%'>
                    <VStack align='start' spacing={1}>
                      <Text fontWeight='bold'>{formatAddress(option)}</Text>
                      {option.propertyType && (
                        <Text fontSize='sm' color='gray.600'>
                          {option.propertyType.charAt(0).toUpperCase() +
                            option.propertyType.slice(1)}
                        </Text>
                      )}
                      {option.price && (
                        <Text fontSize='sm' color='blue.600'>
                          Last sold: £{option.price.toLocaleString()}
                          {option.date && ` (${new Date(option.date).toLocaleDateString()})`}
                        </Text>
                      )}
                    </VStack>
                  </Radio>
                </Box>
              ))}
            </VStack>
          </RadioGroup>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='blue' mr={3} onClick={handleSelect} isDisabled={!selectedAddress}>
            Confirm Selection
          </Button>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddressSelectionModal;
